import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination, fmtId } from './ReportLayout';
import { ReportChartRow, BarCard, DonutCard, groupCount, varianceBuckets, VARIANCE_COLORS } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDT = v => { try { return format(parseISO(v), 'd MMM yy h:mm a'); } catch { return v||'–'; } };
const varCell = v => (
  <span className={clsx('font-medium text-xs', !v ? 'text-slate-400' : Math.abs(v) >= 30 ? 'text-red-600' : 'text-amber-600')}>
    {v != null ? `${v > 0 ? '+' : ''}${v} min` : '–'}
  </span>
);

const COLS = [
  { key:'shift_id',              label:'Shift ID',              render: fmtId },
  { key:'scheduled_start',       label:'Schedul...',   render: fmtDT },
  { key:'clock_in_time',         label:'Clock In',     render: fmtDT },
  { key:'clock_in_variance_min', label:'Start Ti...',  render: varCell },
  { key:'scheduled_end',         label:'Schedul...',   render: fmtDT },
  { key:'clock_out_time',        label:'Clock Out',    render: fmtDT },
  { key:'clock_out_variance_min',label:'End Tim...',   render: varCell },
  { key:'address',               label:'Address' },
  { key:'_team',                 label:'Team',         render: () => '–' },
  { key:'shift_type',            label:'Shift Type' },
  { key:'price_book',            label:'Price Book' },
  { key:'facility',              label:'Facility' },
  { key:'staff_name',            label:'Staff Name' },
  { key:'client',                label:'Client' },
];

export default function ClockVariance() {
  const [data, setData]         = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => { api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients||[])).catch(()=>{}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/clock-variance?${p}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label:'Date Range', value:`${startDate} - ${endDate}` },
    ...(selClient ? [{ label:'Client', value:`${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  return (
    <ReportLayout title={<span className="flex items-center gap-2">Clock In/Out Variance <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 text-[10px] flex items-center justify-center">i</span></span>}
      data={data} filename="clock_variance" columns={COLS} activeChips={chips}
      showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <DonutCard title="Clock-in punctuality" data={varianceBuckets(data, 'clock_in_variance_min')} colors={VARIANCE_COLORS} />
          <BarCard title="Most often late (staff)" data={groupCount(data.filter(r => Number(r.clock_in_variance_min) >= 5), 'staff_name', { limit: 8 })} color="#EF4444" horizontal />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
